/**
 * useFolders Hook
 *
 * Manages folder operations for onboardings.
 * Provides CRUD operations and folder tree utilities.
 *
 * Sprint 3.2 - Folder management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { OnboardingFolderService } from '@modules/firebase-core/services/OnboardingFolderService';
import { OnboardingFolder } from '@modules/firebase-core/converters/onboardingFolderConverter';
import { UseFoldersResult } from '../types/onboardings.types';

/**
 * Options for useFolders hook
 */
export interface UseFoldersOptions {
  /** Project ID */
  projectId: string;
}

/**
 * Hook for managing onboarding folders
 *
 * @param options - Configuration options
 * @returns Folder state and actions
 *
 * @example
 * ```typescript
 * const {
 *   folders,
 *   loading,
 *   createFolder,
 *   renameFolder,
 *   deleteFolder,
 *   getFolderOptions,
 * } = useFolders({ projectId: 'project-123' });
 * ```
 */
export function useFolders(options: UseFoldersOptions): UseFoldersResult {
  const { projectId } = options;

  const [folders, setFolders] = useState<OnboardingFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Services (created once)
  const folderServiceRef = useRef(new OnboardingFolderService());
  const onboardingServiceRef = useRef(new OnboardingService());

  /**
   * Load folders
   */
  const loadFolders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const folderService = folderServiceRef.current;
      const loadedFolders = await folderService.list(projectId);
      setFolders(loadedFolders);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load folders';
      setError(message);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /**
   * Create a new folder
   */
  const createFolder = useCallback(async (name: string): Promise<OnboardingFolder> => {
    const folderService = folderServiceRef.current;
    const newFolder = await folderService.create(projectId, { name });

    // Refresh folders list
    await loadFolders();

    return newFolder;
  }, [projectId, loadFolders]);

  /**
   * Rename a folder
   */
  const renameFolder = useCallback(async (folderId: string, newName: string): Promise<void> => {
    const folderService = folderServiceRef.current;
    await folderService.update(projectId, folderId, { name: newName });

    // Refresh folders list
    await loadFolders();
  }, [projectId, loadFolders]);

  /**
   * Delete a folder
   */
  const deleteFolder = useCallback(async (folderId: string): Promise<void> => {
    const folderService = folderServiceRef.current;
    await folderService.delete(projectId, folderId);

    // Refresh folders list
    await loadFolders();
  }, [projectId, loadFolders]);

  /**
   * Get count of items in a folder
   */
  const getFolderContentsCount = useCallback(async (folderId: string): Promise<number> => {
    const onboardingService = onboardingServiceRef.current;
    const onboardings = await onboardingService.listByFolder(projectId, folderId);
    return onboardings.length;
  }, [projectId]);

  /**
   * Get folder options for selection (e.g., move dialog)
   * Returns array with Root option first, then all folders except the excluded one
   */
  const getFolderOptions = useCallback((excludeFolderId?: string): Array<{ id: string | null; name: string }> => {
    const options: Array<{ id: string | null; name: string }> = [
      { id: null, name: 'Root (no folder)' },
    ];

    folders
      .filter(folder => folder.id !== excludeFolderId)
      .forEach(folder => {
        options.push({ id: folder.id, name: folder.name });
      });

    return options;
  }, [folders]);

  /**
   * Refresh folders list
   */
  const refresh = useCallback(async () => {
    await loadFolders();
  }, [loadFolders]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  return {
    folders,
    loading,
    error,
    createFolder,
    renameFolder,
    deleteFolder,
    getFolderContentsCount,
    getFolderOptions,
    refresh,
  };
}
